import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { aiTag } from '../aiLayer'
import { useChatPrefs } from '../chatPrefs'
import { useChatsDeps } from '../ChatsDeps'
import { Sparkles } from './phoneIcons'

type AiState = { status?: unknown; vibe?: unknown; last_interrupt_type?: unknown }

/** Live facts that can change what the AI is doing in a chat. */
const AI_KINDS = new Set(['handoff', 'ai_sent', 'phase'])

/**
 * Above the composer on a phone (ADR 0035): what the AI is doing in this chat,
 * read-only — "AI is replying to Ann", or "Needs you: asks for custom content".
 * Taking over and handing back need brain endpoints that do not exist yet.
 */
export function AiStrip({ accountId, fanId, name }: { accountId: string; fanId: string; name: string }) {
  const { t } = useTranslation('chats')
  const { t: tc } = useTranslation('chat')
  const { local, subscribe } = useChatsDeps()
  const { names } = useChatPrefs()
  const shown = (names[fanId] || name).split(/\s+/)[0] || ''
  const [state, setState] = useState<AiState | null>(null)
  const [tick, setTick] = useState(0)
  useEffect(() => subscribe(accountId, (ev) => ev.fan_id === fanId && AI_KINDS.has(ev.kind) && setTick((n) => n + 1)), [subscribe, accountId, fanId])
  useEffect(() => {
    let live = true
    local<{ items?: (AiState & { fan_id?: unknown })[] }>('/v1/chats/ai-overview', { fan_ids: [fanId] })
      .then((d) => live && setState((d?.items ?? []).find((it) => String(it.fan_id) === fanId) ?? null))
      .catch(() => live && setState(null))
    return () => {
      live = false
    }
  }, [local, fanId, tick])

  const tag = state ? aiTag(state.status, state.vibe) : null
  if (tag === 'needs_you') {
    const type = String(state?.last_interrupt_type ?? '')
    const reason = type ? tc(`reasons.${type}`, { defaultValue: type.replace(/_/g, ' ') }) : ''
    return (
      <div className="ph-ai-strip is-needs" role="status">
        <FeatherIcon name="alert-circle" size={15} />
        <span>{reason ? t('phone.needsYouWhy', { reason }) : t('tabs.needs_you')}</span>
      </div>
    )
  }
  if (!tag || tag === 'ignores') return null
  return (
    <div className="ph-ai-strip" role="status">
      <Sparkles size={15} />
      <span>{shown ? t('phone.aiReplyingTo', { name: shown }) : t('phone.aiReplying')}</span>
    </div>
  )
}
